import { useState, useCallback, useEffect } from "react";
import api from "../api/api";

export interface BackupRecord {
  id: string;
  backup_type: "full" | "environment";
  status: "pending" | "running" | "completed" | "failed";
  environment: number | null;
  environment_name: string | null;
  blob_name: string;
  size_bytes: number | null;
  duration_seconds: number | null;
  patient_count: number | null;
  error_message: string;
  triggered_by: number | null;
  triggered_by_email: string | null;
  created_at: string;
}

export interface RestorePreview {
  total_records: number;
  new_records: number;
  exclusive_records: number;
  shared_records: number;
  shared_details: Array<{
    model: string;
    pk: number | string;
    name: string;
    other_environments: string[];
  }>;
}

export function useEnvironmentBackups(environmentId?: number) {
  const [records, setRecords] = useState<BackupRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBackups = useCallback(async () => {
    if (!environmentId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/environments/${environmentId}/backups/`);
      setRecords(res.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to fetch backups");
    } finally {
      setLoading(false);
    }
  }, [environmentId]);

  useEffect(() => {
    if (environmentId) fetchBackups();
  }, [fetchBackups, environmentId]);

  const triggerBackup = useCallback(async () => {
    if (!environmentId) return;
    const res = await api.post(`/environments/${environmentId}/backup/`);
    return res.data;
  }, [environmentId]);

  const triggerPreviewRestore = useCallback(
    async (backupId: string): Promise<RestorePreview> => {
      if (!environmentId) throw new Error("No environment selected");
      const res = await api.post(`/environments/${environmentId}/restore/preview/`, {
        backup_id: backupId,
      });
      return res.data;
    },
    [environmentId]
  );

  const triggerRestore = useCallback(
    async (
      backupId: string,
      conflictMode: "skip_shared" | "overwrite_shared" | "per_record" = "skip_shared",
      perRecordDecisions?: Array<{ model: string; pk: string | number; action: "skip" | "overwrite" }>
    ) => {
      if (!environmentId) throw new Error("No environment selected");
      const res = await api.post(`/environments/${environmentId}/restore/`, {
        backup_id: backupId,
        conflict_mode: conflictMode,
        per_record_decisions: perRecordDecisions,
      });
      return res.data;
    },
    [environmentId]
  );

  return {
    records,
    loading,
    error,
    refetch: fetchBackups,
    triggerBackup,
    triggerPreviewRestore,
    triggerRestore,
  };
}

export function useAdminBackups() {
  const [records, setRecords] = useState<BackupRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBackups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get("/admin/backups/");
      setRecords(res.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to fetch backups");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  const triggerFullBackup = useCallback(async () => {
    const res = await api.post("/admin/backups/full/");
    return res.data;
  }, []);

  return { records, loading, error, refetch: fetchBackups, triggerFullBackup };
}
